import React, { useState, useRef, useCallback } from 'react';
import {
  Typography,
  Box,
  Button,
  Divider,
  Paper,
  Grid,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Tooltip,
  Chip,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slider,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Image as ImageIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useAppContext } from '../context/AppContext';
import { LayerElement } from '../types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`layer-tabpanel-${index}`}
      aria-labelledby={`layer-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const ImageUpload: React.FC = () => {
  const { layers, updateLayer, setCurrentStep, config, setLayers } = useAppContext();
  const [selectedTab, setSelectedTab] = useState(0);
  const [newElementName, setNewElementName] = useState('');
  const [newElementWeight, setNewElementWeight] = useState('1');
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [editingElement, setEditingElement] = useState<LayerElement | null>(null);
  const [editName, setEditName] = useState('');
  const [editWeight, setEditWeight] = useState(1);
  const [openDialog, setOpenDialog] = useState(false);
  const [restrictionsDialog, setRestrictionsDialog] = useState(false);
  const [selectedLayerForRestriction, setSelectedLayerForRestriction] = useState<number | null>(null);
  const [selectedElementsForRestriction, setSelectedElementsForRestriction] = useState<number[]>([]);
  const [restrictionType] = useState<'incompatible'>('incompatible');

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  // Fonction pour vérifier si une image est valide
  const validateImage = (file: File): Promise<boolean> => {
    // Allow files named "NONE" or similar to represent the absence of an element
    if (file.name.toUpperCase().includes('NONE')) {
      return Promise.resolve(true);
    }
    
    return new Promise((resolve) => {
      // Function to check if an image is valid
      const img = new Image();
      img.onload = () => {
        resolve(true);
      };
      img.onerror = () => {
        resolve(false);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const handleAddElement = async (layerId: number, files: FileList | null) => {
    if (!files || files.length === 0 || !newElementName.trim()) return;
    
    setIsUploading(true);
    
    try {
      const layerIndex = layers.findIndex(layer => layer.id === layerId);
      if (layerIndex === -1) return;
      
      const layer = layers[layerIndex];
      const weight = parseInt(newElementWeight) || 1;
      
      // Vérifier si l'image est valide
      const isValid = await validateImage(files[0]);
      if (!isValid) {
        throw new Error(`L'image "${files[0].name}" n'est pas valide ou n'est pas dans un format pris en charge.`);
      }
      
      // Create new element
      const newElement = {
        id: Date.now(),
        name: newElementName.trim(),
        weight,
        image: files[0]
      };
      
      // Update layer with new element
      const updatedElements = [...layer.elements, newElement];
      const updatedLayer = {
        ...layer,
        elements: updatedElements
      };
      const updatedLayers = layers.map(l => 
        l.id === layerId ? updatedLayer : l
      );
      setLayers(updatedLayers);
      
      // Reset form
      setNewElementName('');
      setNewElementWeight('1');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error adding element:', error);
      alert(error instanceof Error ? error.message : 'Une erreur est survenue lors de l\'ajout de l\'élément');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveElement = (layerId: number, elementId: number) => {
    const layerIndex = layers.findIndex(layer => layer.id === layerId);
    if (layerIndex === -1) return;
    
    const layer = layers[layerIndex];
    const updatedElements = layer.elements.filter(element => element.id !== elementId);
    
    const updatedLayer = {
      ...layer,
      elements: updatedElements
    };
    const updatedLayers = layers.map(l => 
      l.id === layerId ? updatedLayer : l
    );
    setLayers(updatedLayers);
  };

  const handleNext = () => {
    // Check if at least one layer has elements
    const hasElements = layers.some(layer => layer.elements.length > 0);
    
    if (hasElements) {
      setCurrentStep(3);
    }
  };

  const handleBack = () => {
    setCurrentStep(1);
  };

  const handleBulkUpload = useCallback(async (layerId: number, files: FileList | null) => {
    if (!files || files.length === 0) return;
    
    setIsUploading(true);
    
    try {
      const layerIndex = layers.findIndex(layer => layer.id === layerId);
      if (layerIndex === -1) return;
      
      const layer = layers[layerIndex];
      const newElements: LayerElement[] = [];
      const invalidFiles: string[] = [];
      
      // Process each file
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Check if the image is valid
        const isValid = await validateImage(file);
        if (!isValid) {
          invalidFiles.push(file.name);
          continue;
        }
        
        // Extract name and weight using the rarity delimiter
        const nameWithoutExtension = file.name.slice(0, file.name.lastIndexOf('.'));
        const parts = nameWithoutExtension.split(config.rarityDelimiter);
        
        let name = parts[0];
        let weight = 1;
        
        if (parts.length > 1) {
          const parsedWeight = parseInt(parts[1]);
          if (!isNaN(parsedWeight)) {
            weight = parsedWeight;
          }
        }
        
        newElements.push({
          id: Date.now() + i,
          name,
          weight,
          image: file
        });
      }
      
      // Display an error message for invalid files
      if (invalidFiles.length > 0) {
        alert(`The following files could not be loaded because they are invalid or not in a supported format: ${invalidFiles.join(', ')}`);
      }
      
      // Update layer with new elements
      if (newElements.length > 0) {
        const updatedElements = [...layer.elements, ...newElements];
        const updatedLayer = {
          ...layer,
          elements: updatedElements
        };
        const updatedLayers = layers.map(l => 
          l.id === layerId ? updatedLayer : l
        );
        setLayers(updatedLayers);
      }
    } catch (error) {
      console.error('Error bulk uploading:', error);
      alert('Une erreur est survenue lors du téléchargement en masse');
    } finally {
      setIsUploading(false);
    }
  }, [layers, setLayers, config.rarityDelimiter]);

  const handleEditElement = (element: LayerElement) => {
    setEditingElement(element);
    setEditName(element.name);
    setEditWeight(element.weight);
    setOpenDialog(true);
  };

  const handleSaveElement = () => {
    if (!editingElement) return;

    const layerIndex = layers.findIndex(layer => layer.id === editingElement.id);
    if (layerIndex === -1) return;
    
    const layer = layers[layerIndex];
    const updatedElements = layer.elements.map(el => 
      el.id === editingElement.id 
        ? { ...el, name: editName, weight: editWeight } 
        : el
    );
    const updatedLayer = {
      ...layer,
      elements: updatedElements
    };
    const updatedLayers = layers.map(l => 
      l.id === editingElement.id ? updatedLayer : l
    );
    setLayers(updatedLayers);
    setOpenDialog(false);
  };

  const handleEditRestrictions = (element: LayerElement) => {
    setEditingElement(element);
    setRestrictionsDialog(true);
  };

  const handleSaveRestrictions = () => {
    if (!editingElement || !selectedLayerForRestriction || selectedElementsForRestriction.length === 0) return;

    const updatedElements = layers.map(layer => {
      if (layer.elements.some(el => el.id === editingElement.id)) {
        return {
          ...layer,
          elements: layer.elements.map(element => {
            if (element.id === editingElement.id) {
              return {
                ...element,
                restrictions: {
                  incompatibleWith: [
                    {
                      layerId: selectedLayerForRestriction,
                      elementIds: selectedElementsForRestriction
                    }
                  ]
                }
              };
            }
            return element;
          })
        };
      }
      return layer;
    });

    setLayers(updatedElements);
    setRestrictionsDialog(false);
    setEditingElement(null);
    setSelectedLayerForRestriction(null);
    setSelectedElementsForRestriction([]);
  };

  const handleCloseRestrictionsDialog = () => {
    setRestrictionsDialog(false);
    setEditingElement(null);
    setSelectedLayerForRestriction(null);
    setSelectedElementsForRestriction([]);
  };

  const handleRemoveRestriction = (elementId: number, restrictionType: 'incompatibleWith', layerId: number, elementIds: number[]) => {
    const updatedElements = layers.map(layer => {
      if (layer.elements.some(el => el.id === elementId)) {
        return {
          ...layer,
          elements: layer.elements.map(element => {
            if (element.id === elementId && element.restrictions) {
              return {
                ...element,
                restrictions: {
                  incompatibleWith: element.restrictions.incompatibleWith?.filter(
                    r => !(r.layerId === layerId && r.elementIds.every(id => elementIds.includes(id)))
                  )
                }
              };
            }
            return element;
          })
        };
      }
      return layer;
    });

    setLayers(updatedElements);
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <CloudUploadIcon color="primary" sx={{ mr: 1 }} />
        <Typography variant="h5" component="h1">
          Image Upload
        </Typography>
      </Box>

      <Divider sx={{ mb: 4 }} />

      {layers.length === 0 ? (
        <Alert severity="warning" sx={{ mb: 3 }}>
          You need to create layers first before you can upload images.
        </Alert>
      ) : (
        <>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Layer Elements
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Tip:</strong> You can upload a transparent PNG image named "NONE.png" to represent the absence of a trait.
                When an image named "NONE" is selected during generation, that trait will not appear in the metadata.
                This is useful for creating rarity where some NFTs don't have certain traits.
                You can also apply rarity weights to NONE files using the rarity delimiter, for example: "NONE#90.png" for a 90% chance of the trait being absent.
              </Typography>
            </Alert>

            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Restrictions:</strong> You can set up restrictions between elements to control which combinations are allowed during generation.
                For example, if you want to prevent certain elements from appearing together, you can set them as incompatible.
                To manage restrictions, click on the gear icon <SettingsIcon sx={{ fontSize: 16, verticalAlign: 'middle', mx: 0.5 }} /> next to any element.
              </Typography>
            </Alert>
          </Box>
          
          <Paper elevation={3} sx={{ mb: 4 }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={selectedTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="layer tabs"
              >
                {layers.map((layer, index) => (
                  <Tab 
                    key={layer.id} 
                    label={
                      <Box display="flex" alignItems="center">
                        {layer.name}
                        <Chip 
                          size="small" 
                          label={layer.elements.length} 
                          sx={{ ml: 1, height: 20 }} 
                        />
                      </Box>
                    } 
                    id={`layer-tab-${index}`}
                    aria-controls={`layer-tabpanel-${index}`}
                  />
                ))}
              </Tabs>
            </Box>

            {layers.map((layer, index) => (
              <TabPanel key={layer.id} value={selectedTab} index={index}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={5}>
                    <Typography variant="h6" gutterBottom>
                      Add element to {layer.name}
                    </Typography>
                    
                    <TextField
                      fullWidth
                      label="Element Name"
                      value={newElementName}
                      onChange={(e) => setNewElementName(e.target.value)}
                      margin="normal"
                      variant="outlined"
                      placeholder="Ex: Red, Blue, Smile..."
                    />
                    
                    <TextField
                      fullWidth
                      label="Rarity Weight"
                      type="number"
                      value={newElementWeight}
                      onChange={(e) => setNewElementWeight(e.target.value)}
                      margin="normal"
                      variant="outlined"
                      helperText="Higher values = more common"
                      InputProps={{
                        inputProps: { min: 1 }
                      }}
                    />
                    
                    <Box sx={{ mt: 2, mb: 2 }}>
                      <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        id={`element-upload-${layer.id}`}
                        type="file"
                        onChange={(e) => handleAddElement(layer.id, e.target.files)}
                        ref={fileInputRef}
                      />
                      <label htmlFor={`element-upload-${layer.id}`}>
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={isUploading ? <CircularProgress size={20} /> : <ImageIcon />}
                          disabled={isUploading || !newElementName.trim()}
                          fullWidth
                        >
                          {isUploading ? 'Uploading...' : 'Select Image'}
                        </Button>
                      </label>
                    </Box>
                    
                    <Divider sx={{ my: 3 }} />
                    
                    <Typography variant="h6" gutterBottom>
                      OR Bulk Upload
                    </Typography>
                    
                    <Typography variant="body2" color="textSecondary" paragraph>
                      Upload multiple images at once. File names should follow the format: name{config.rarityDelimiter}weight.png
                    </Typography>
                    
                    <Box sx={{ mt: 2 }}>
                      <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        id={`bulk-upload-${layer.id}`}
                        type="file"
                        multiple
                        onChange={(e) => handleBulkUpload(layer.id, e.target.files)}
                      />
                      <label htmlFor={`bulk-upload-${layer.id}`}>
                        <Button
                          variant="contained"
                          color="primary"
                          component="span"
                          startIcon={isUploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                          disabled={isUploading}
                          fullWidth
                          sx={{ py: 1 }}
                        >
                          {isUploading ? 'Uploading...' : 'Bulk Upload Images'}
                        </Button>
                      </label>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={7}>
                    <Typography variant="h6" gutterBottom>
                      Elements in {layer.name} ({layer.elements.length})
                    </Typography>
                    
                    {layer.elements.length === 0 ? (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        No elements yet. Add your first element to this layer.
                      </Alert>
                    ) : (
                      <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                        {layer.elements.map((element) => (
                          <ListItem
                            key={element.id}
                            secondaryAction={
                              <Box>
                                <IconButton
                                  edge="end"
                                  aria-label="restrictions"
                                  onClick={() => handleEditRestrictions(element)}
                                >
                                  <SettingsIcon />
                                </IconButton>
                                <IconButton
                                  edge="end"
                                  aria-label="delete"
                                  onClick={() => handleRemoveElement(layer.id, element.id)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            }
                          >
                            <ListItemAvatar>
                              <Avatar
                                variant="rounded"
                                src={element.image ? URL.createObjectURL(element.image) : ''}
                                alt={element.name}
                                sx={{ 
                                  width: 60, 
                                  height: 60, 
                                  mr: 1,
                                  imageRendering: 'pixelated'
                                }}
                              />
                            </ListItemAvatar>
                            <ListItemText
                              primary={element.name}
                              secondary={
                                <>
                                  <Box component="span" sx={{ display: 'block', fontSize: '0.875rem', color: 'text.secondary' }}>
                                    Weight: {element.weight}
                                  </Box>
                                  <Box component="span" sx={{ display: 'block', fontSize: '0.875rem', color: 'text.secondary' }}>
                                    File: {element.image ? element.image.name : 'No file'}
                                  </Box>
                                  {element.restrictions && (
                                    <>
                                      {element.restrictions.incompatibleWith && element.restrictions.incompatibleWith.length > 0 && (
                                        <Box component="span" sx={{ display: 'block', fontSize: '0.875rem', color: 'error.main', mt: 0.5 }}>
                                          Incompatible with: {element.restrictions.incompatibleWith.map((restriction, index) => {
                                            const layer = layers.find(l => l.id === restriction.layerId);
                                            const elements = layer?.elements.filter(e => restriction.elementIds.includes(e.id));
                                            return (
                                              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {`${layer?.name} (${elements?.map(e => e.name).join(', ')})`}
                                                <IconButton
                                                  size="small"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRemoveRestriction(element.id, 'incompatibleWith', restriction.layerId, restriction.elementIds);
                                                  }}
                                                  sx={{ p: 0.5 }}
                                                >
                                                  <DeleteIcon fontSize="small" />
                                                </IconButton>
                                              </Box>
                                            );
                                          })}
                                        </Box>
                                      )}
                                    </>
                                  )}
                                </>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Grid>
                </Grid>
              </TabPanel>
            ))}
          </Paper>
        </>
      )}

      <Box display="flex" justifyContent="space-between" mt={4}>
        <Button
          variant="outlined"
          onClick={handleBack}
          startIcon={<ArrowBackIcon />}
        >
          Back
        </Button>
        {layers.length > 0 && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleNext}
            endIcon={<ArrowForwardIcon />}
            disabled={!layers.some(layer => layer.elements.length > 0)}
          >
            Next: Add 1/1 NFTs to the collection
          </Button>
        )}
      </Box>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Edit Element</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Element Name"
            fullWidth
            variant="outlined"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            sx={{ mb: 2 }}
          />
          
          <Typography gutterBottom>
            Rarity Weight
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Slider
              value={editWeight}
              min={1}
              max={100}
              step={1}
              onChange={(_, value) => setEditWeight(value as number)}
              sx={{ flexGrow: 1, mr: 2 }}
            />
            <TextField
              value={editWeight}
              onChange={(e) => {
                const value = parseInt(e.target.value);
                if (!isNaN(value) && value >= 1) {
                  setEditWeight(value);
                }
              }}
              InputProps={{
                inputProps: { min: 1 },
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              sx={{ width: '100px' }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveElement}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={restrictionsDialog}
        onClose={handleCloseRestrictionsDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Manage Restrictions</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Select incompatible elements
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Layer</InputLabel>
              <Select
                value={selectedLayerForRestriction || ''}
                onChange={(e) => setSelectedLayerForRestriction(Number(e.target.value))}
                label="Layer"
              >
                {layers
                  .filter(layer => !layer.elements.some(el => el.id === editingElement?.id))
                  .map(layer => (
                    <MenuItem key={layer.id} value={layer.id}>
                      {layer.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            {selectedLayerForRestriction && (
              <FormControl fullWidth>
                <InputLabel>Elements</InputLabel>
                <Select
                  multiple
                  value={selectedElementsForRestriction}
                  onChange={(e) => setSelectedElementsForRestriction(e.target.value as number[])}
                  label="Elements"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const element = layers
                          .find(l => l.id === selectedLayerForRestriction)
                          ?.elements.find(el => el.id === value);
                        return (
                          <Chip
                            key={value}
                            label={element?.name || value}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {layers
                    .find(l => l.id === selectedLayerForRestriction)
                    ?.elements.map(element => (
                      <MenuItem key={element.id} value={element.id}>
                        <Checkbox checked={selectedElementsForRestriction.includes(element.id)} />
                        <ListItemText primary={element.name} />
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRestrictionsDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveRestrictions}
            variant="contained"
            disabled={!selectedLayerForRestriction || selectedElementsForRestriction.length === 0}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ImageUpload; 